import { Button } from '@/components/ui/Button';
import { dashboardRhService } from '../services/dashboard-rh.service';
import { useMutation } from '@tanstack/react-query';
import { Download, FileText, Presentation, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  dashboardUuid: string;
  folderName: string;
}

export function ExportModal({
  isOpen,
  onClose,
  dashboardUuid,
  folderName,
}: ExportModalProps) {
  const { t } = useTranslation();
  const [selectedFormat, setSelectedFormat] = useState<
    'pdf' | 'powerpoint'
  >('pdf');

  const exportMutation = useMutation({
    mutationFn: (format: 'pdf' | 'powerpoint') =>
      dashboardRhService.exportDashboard(dashboardUuid, format),
    onSuccess: async (blob, format) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const extensions = {
        pdf: 'pdf',
        powerpoint: 'pptx',
      };
      
      a.download = `tableau-bord-rh-${folderName}-${new Date().toISOString().split('T')[0]}.${extensions[format]}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      onClose();
    },
  });

  const handleExport = () => {
    exportMutation.mutate(selectedFormat);
  };

  if (!isOpen) return null;

  const formats = [
    {
      value: 'pdf' as const,
      label: t('dashboardRh.export.pdf', 'PDF'),
      description: t(
        'dashboardRh.export.pdfDescription',
        'Document PDF optimisé pour l\'impression'
      ),
      icon: FileText,
      color: 'bg-red-50 text-red-600',
    },

    {
      value: 'powerpoint' as const,
      label: t('dashboardRh.export.powerpoint', 'PowerPoint'),
      description: t(
        'dashboardRh.export.powerpointDescription',
        'Présentation PowerPoint'
      ),
      icon: Presentation,
      color: 'bg-orange-50 text-orange-600',
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-neutral-900/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
              <Download className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-neutral-900">
                {t('dashboardRh.export.title', 'Exporter le tableau de bord')}
              </h2>
              <p className="text-sm text-neutral-500">{folderName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Format Selection */}
        <div className="mb-6 space-y-3">
          {formats.map(format => {
            const Icon = format.icon;
            return (
              <button
                key={format.value}
                onClick={() => setSelectedFormat(format.value)}
                className={`w-full rounded-lg border-2 p-4 text-left transition-colors ${
                  selectedFormat === format.value
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-neutral-200 bg-white hover:border-neutral-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                      selectedFormat === format.value
                        ? 'bg-primary-100'
                        : format.color
                    }`}
                  >
                    <Icon
                      className={`h-5 w-5 ${
                        selectedFormat === format.value
                          ? 'text-primary-600'
                          : format.color.split(' ')[1]
                      }`}
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-neutral-900">
                      {format.label}
                    </h3>
                    <p className="text-sm text-neutral-600">
                      {format.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Error Message */}
        {exportMutation.isError && (
          <div className="mb-6 rounded-lg bg-error-50 p-4">
            <p className="text-sm text-error-800">
              {t('dashboardRh.export.error', 'Erreur lors de l\'export')}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button
            onClick={handleExport}
            disabled={exportMutation.isPending}
            className="flex-1"
          >
            {exportMutation.isPending
              ? t('common.loading', 'Chargement...')
              : t('dashboardRh.export.export', 'Exporter')}
          </Button>
        </div>
      </div>
    </div>
  );
}

